import { FC, ChangeEvent, useState } from "react"
import styled from "styled-components"
import Image from "next/image"

import { ProductVariant } from "shopify-storefront-api-typings"

import { getColorValue, getOptionValue } from "@lib/utils"

import { Price } from "@components/common"
import { Select } from "@components/ui"
import { Checkbox } from "./index"
import { ContentfulProductWithShopifyData } from "@lib/types"

interface Props {
  product: ContentfulProductWithShopifyData
  selected: ProductVariant[]
  handleChecked: (e: ChangeEvent<HTMLInputElement>) => void
}

export const CompleteTheLookThumbnail: FC<Props> = ({
  product,
  selected,
  handleChecked,
}) => {
  const { sys, slug, title, color, shopifyProduct } = product

  const options = shopifyProduct?.variants?.edges?.map(({ node }) => ({
    value: node.id,
    label: getOptionValue(node.selectedOptions),
  }))

  const [selectedVariant, setSelectedVariant] = useState<ProductVariant>(
    shopifyProduct?.variants?.edges[0]?.node
  )

  const handleSelect = (e: any) => {
    setSelectedVariant(
      shopifyProduct.variants.edges.filter(({ node }) => e.value === node.id)[0]
        ?.node
    )
  }

  return (
    <Article>
      <ImageWrapper>
        <Label htmlFor={slug}>
          <Checkbox
            name={slug}
            checked={selected.some(s => s.id === selectedVariant.id)}
            value={JSON.stringify(selectedVariant)}
            onChange={handleChecked}
          />
          {shopifyProduct && (
            <Image
              src={shopifyProduct.images.edges[0].node.transformedSrc}
              width={200}
              height={310}
              layout="responsive"
            />
          )}
        </Label>
        <SelectWrapper>
          <Select id={sys.id} options={options} onChange={handleSelect} />
        </SelectWrapper>
      </ImageWrapper>
      <TextWrapper>
        <h1>{title}</h1>
        <FeaturedVariant>
          {color?.name || getColorValue(selectedVariant.selectedOptions)} |{" "}
          <Price variant={selectedVariant} />
        </FeaturedVariant>
      </TextWrapper>
    </Article>
  )
}

const Article = styled.div`
  width: 100%;
`
const SelectWrapper = styled.div`
  position: absolute;
  width: 100%;
  bottom: 1.4rem;
  padding: 0 1.35rem;
  opacity: 0;
  transition: opacity 0.25s ease-out;
`

const ImageWrapper = styled.div`
  position: relative;
  margin: 0 0 2rem;
  padding-top: 150%;

  &:hover {
    ${SelectWrapper} {
      opacity: 1;
    }
  }
`

const Label = styled.label`
  position: absolute;
  top: 0;
  width: 100%;
  height: 100%;
`

const TextWrapper = styled.div`
  white-space: pre-wrap;
  text-align: center;
  color: ${({ theme }) => theme.color.black};

  h1,
  h2,
  h3,
  h4 {
    margin: 0 0 1.4rem;
    font-size: 1.8rem;
    line-height: 2.4rem;
    font-weight: 300;
  }

  @media (min-width: ${({ theme }) => theme.breakpoint.md}px) {
    height: ${({ theme }) => theme.productThumbnail.textWrapper.height}rem;
    overflow: hidden;

    h1,
    h2,
    h3,
    h4 {
      margin: 0 0 1.6rem;
      font-size: 2rem;
    }
  }
`

const FeaturedVariant = styled.span`
  color: ${({ theme }) => theme.color.grey};
  font-size: 1.5rem;
  line-height: 2.6rem;
  font-weight: 400;
`
